import axiosInstance from "../../scenes/utils/axiosInstance";
import { API_ENDPOINTS } from "../../config/api";
import { withExportLang, getExportLangParam } from "../../utils/getExportLangParam";
import { getDoctorSessionCacheKey } from "../../utils/authSession";
import { clearFileCache } from "./FileService";

const formCache = new Map();
const inFlightFormRequests = new Map();
const singleFormCache = new Map();
const inFlightSingleFormRequests = new Map();
const CACHE_TTL_MS = 3 * 60 * 1000;

export function clearFormCache() {
  formCache.clear();
  inFlightFormRequests.clear();
  singleFormCache.clear();
  inFlightSingleFormRequests.clear();
}

export default class FormService {
  createForm = async (data) => {
    try {
      const response = await axiosInstance.post(
        API_ENDPOINTS.FORMS.CREATE,
        data
      );
      clearFormCache();
      clearFileCache();
      return response.data;
    } catch (error) {
      console.error("Bir sorun oluştu:", error.response?.data || error.message);
      throw error; // Yukarıda da try/catch varsa, orada yakalanır
    }
  };

  getCachedFormInfo = (formId, options = {}) => {
    if (!formId) return null;
    const _uid = getDoctorSessionCacheKey();
    const cacheKey = `${_uid || 'anon'}_${formId}_${JSON.stringify(options.params || {})}`;
    const cached = singleFormCache.get(cacheKey);
    const now = Date.now();
    if (cached && now - cached.timestamp < CACHE_TTL_MS) {
      return cached.data;
    }
    return null;
  };

  getFormInfo = async (formId, options = {}) => {
    try {
      const _uid = getDoctorSessionCacheKey();
      const cacheKey = `${_uid || 'anon'}_${formId}_${JSON.stringify(options.params || {})}`;
      const now = Date.now();
      const cached = singleFormCache.get(cacheKey);
      if (!options.forceRefresh && cached && now - cached.timestamp < CACHE_TTL_MS) {
        return cached.data;
      }
      if (inFlightSingleFormRequests.has(cacheKey)) {
        return await inFlightSingleFormRequests.get(cacheKey);
      }

      const fetchPromise = (async () => {
        try {
          const response = await axiosInstance.get(
            API_ENDPOINTS.FORMS.BY_ID(formId),
            options
          );
          if (response?.data?.id) {
            singleFormCache.set(cacheKey, { data: response.data, timestamp: Date.now() });
          }
          return response.data;
        } finally {
          inFlightSingleFormRequests.delete(cacheKey);
        }
      })();

      inFlightSingleFormRequests.set(cacheKey, fetchPromise);
      return await fetchPromise;
    } catch (error) {
      if (
        error?.name === "CanceledError" ||
        error?.name === "AbortError" ||
        error?.code === "ERR_CANCELED"
      ) {
        throw error;
      }
      console.log("Bir sorun oluştu: ", error.response?.data || error.message);
      throw error;
    }
  };

  getAgeScoreScatter = async (formId) => {
    try {
      const response = await axiosInstance.get(
        API_ENDPOINTS.FORMS.AGE_SCORE_SCATTER(formId)
      );
      return response.data;
    } catch (error) {
      console.error("Age-score scatter yüklenemedi:", error.response?.data || error.message);
      throw error;
    }
  };

  /**
   * Cached AI insight summary only (404 if missing).
   * @param {{ locale?: string }} [opts]
   */
  getPatientFormAiSummary = async (formId, patientId, opts = {}) => {
    try {
      const response = await axiosInstance.get(
        API_ENDPOINTS.FORMS.INSIGHTS_AI_SUMMARY_PATIENT(formId, patientId),
        { params: { locale: opts.locale || "tr" } }
      );
      return response.data;
    } catch (error) {
      if (error.response?.status === 404) return null;
      console.error(
        "AI insight summary okunamadı:",
        error.response?.data || error.message
      );
      throw error;
    }
  };

  /**
   * Generate or reuse cached AI insight summary.
   * @param {{ locale?: string, force?: boolean }} [opts]
   */
  generatePatientFormAiSummary = async (formId, patientId, opts = {}) => {
    try {
      const response = await axiosInstance.post(
        API_ENDPOINTS.FORMS.INSIGHTS_AI_SUMMARY_PATIENT(formId, patientId),
        { locale: opts.locale || "tr", force: Boolean(opts.force) },
        { params: { locale: opts.locale || "tr", force: opts.force ? "true" : undefined } }
      );
      return response.data;
    } catch (error) {
      console.error(
        "AI insight summary üretilemedi:",
        error.response?.data || error.message
      );
      throw error;
    }
  };

  /** Cached form-level (population) AI summary (404 → null). */
  getFormAiSummary = async (formId, opts = {}) => {
    try {
      const response = await axiosInstance.get(
        API_ENDPOINTS.FORMS.INSIGHTS_AI_SUMMARY(formId),
        { params: { locale: opts.locale || "tr" } }
      );
      return response.data;
    } catch (error) {
      if (error.response?.status === 404) return null;
      console.error(
        "Form AI summary okunamadı:",
        error.response?.data || error.message
      );
      throw error;
    }
  };

  /** Generate or reuse form-level AI summary. */
  generateFormAiSummary = async (formId, opts = {}) => {
    try {
      const response = await axiosInstance.post(
        API_ENDPOINTS.FORMS.INSIGHTS_AI_SUMMARY(formId),
        { locale: opts.locale || "tr", force: Boolean(opts.force) },
        { params: { locale: opts.locale || "tr", force: opts.force ? "true" : undefined } }
      );
      return response.data;
    } catch (error) {
      console.error(
        "Form AI summary üretilemedi:",
        error.response?.data || error.message
      );
      throw error;
    }
  };

  updateForm = async (formId, updatedFormData) => {
    try {
      const response = await axiosInstance.put(
        API_ENDPOINTS.FORMS.UPDATE(formId),
        updatedFormData
      );
      clearFormCache();
      clearFileCache();
      return response.data;
    } catch (error) {
      console.error("Bir sorun oluştu:", error.response?.data || error.message);
      throw error;
    }
  };
  checkKioskEligibility = async (formId, patientId) => {
    const response = await axiosInstance.post(
      API_ENDPOINTS.FORMS.KIOSK_ELIGIBILITY(formId),
      { patientId }
    );
    return response.data;
  };

  formAnswer = async (data) => {
    try {
      const response = await axiosInstance.post(
        API_ENDPOINTS.FORM_ANSWERS.CREATE,
        data
      );
      return response.data;
    } catch (error) {
      console.error("Bir sorun oluştu:", error.response?.data || error.message);
      throw error;
    }
  };
  getAllForms = async (options = {}) => {
    try {
      const _uid = getDoctorSessionCacheKey();
      const cacheKey = JSON.stringify({ _uid, ...options });
      const now = Date.now();
      const cached = formCache.get(cacheKey);
      if (cached && now - cached.timestamp < CACHE_TTL_MS) {
        return cached.data;
      }
      if (inFlightFormRequests.has(cacheKey)) {
        return await inFlightFormRequests.get(cacheKey);
      }

      const fetchPromise = (async () => {
        try {
          const response = await axiosInstance.get(API_ENDPOINTS.FORMS.ALL, options);
          formCache.set(cacheKey, { data: response.data, timestamp: Date.now() });
          return response.data;
        } finally {
          inFlightFormRequests.delete(cacheKey);
        }
      })();

      inFlightFormRequests.set(cacheKey, fetchPromise);
      return await fetchPromise;
    } catch (error) {
      console.error("Bir sorun oluştu:", error.response?.data || error.message);
      throw error;
    }
  };
  getAllFormAnswers = async (params = {}) => {
    try {
      const response = await axiosInstance.get(
        API_ENDPOINTS.FORM_ANSWERS.ALL,
        { params }
      );
      return response.data;
    } catch (error) {
      console.error("Bir sorun oluştu:", error.response?.data || error.message);
      throw error;
    }
  };

  getFormAnswersByFormId = async (
    formId,
    { pageIndex = 0, pageSize = 10, sortBy = "answered_at", sortOrder = "DESC" } = {}
  ) => {
    try {
      const response = await axiosInstance.get(API_ENDPOINTS.FORM_ANSWERS.ALL, {
        params: { formId },
        headers: {
          "x-bone-pageindex": String(pageIndex),
          "x-bone-limit": String(pageSize),
          "x-bone-sortby": sortBy,
          "x-bone-sortorder": sortOrder,
        },
      });
      return {
        data: Array.isArray(response.data) ? response.data : [],
        totalCount: Number(
          response.headers?.["x-bone-totalcount"] ||
            response.headers?.["x-total-count"] ||
            0
        ),
        totalPages: Number(response.headers?.["x-total-pages"] || 1),
      };
    } catch (error) {
      console.error(
        "Form cevapları alınamadı:",
        error.response?.data || error.message
      );
      throw error;
    }
  };

  getRecentFormAnswers = async (limit = 4) => {
    try {
      const response = await axiosInstance.get(API_ENDPOINTS.FORM_ANSWERS.ALL, {
        headers: {
          "x-bone-pageindex": "0",
          "x-bone-limit": String(limit),
          "x-bone-sortby": "answered_at",
          "x-bone-sortorder": "DESC",
        },
      });
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      console.error("Son form cevapları alınamadı:", error.response?.data || error.message);
      throw error;
    }
  };
  getAnsweredFormInfo = async (formId) => {
    try {
      const response = await axiosInstance.get(
        API_ENDPOINTS.FORM_ANSWERS.BY_ID(formId)
      );
      return response.data;
    } catch (error) {
      console.error("Bir sorun oluştu:", error.response?.data || error.message);
      throw error;
    }
  };

  exportAnsweredFormExcel = async (formId) => {
    try {
      const response = await axiosInstance.get(
        API_ENDPOINTS.FORM_ANSWERS.EXPORT_EXCEL(formId),
        { params: getExportLangParam(), responseType: "blob" }
      );
      return response;
    } catch (error) {
      console.error("Bir sorun oluştu:", error.response?.data || error.message);
      throw error;
    }
  };

  exportFormPatientsExcel = async (formId, filters = {}) => {
    return axiosInstance.get(API_ENDPOINTS.FORMS.EXPORT_PATIENTS_EXCEL(formId), {
      params: withExportLang(filters),
      responseType: "blob",
    });
  };

  deleteForm = async (formId) => {
    try {
      const response = await axiosInstance.delete(
        API_ENDPOINTS.FORMS.DELETE(formId)
      );
      clearFormCache();
      clearFileCache();
      return response.data;
    } catch (error) {
      console.error("Bir sorun oluştu:", error.response?.data || error.message);
      throw error;
    }
  };

  sendFormEmail = async (data, formId, idempotencyKey) => {
    try {
      const response = await axiosInstance.post(
        API_ENDPOINTS.FORMS.INVITATIONS(formId),
        data,
        { headers: idempotencyKey ? { "Idempotency-Key": idempotencyKey } : undefined }
      );
      return response.data;
    } catch (error) {
      console.error("Bir sorun oluştu:", error.response?.data || error.message);
      throw error;
    }
  };

  getPublicFormInfo = async (formToken) => {
    try {
      const response = await axiosInstance.get(
        API_ENDPOINTS.PUBLIC.FORM(formToken),
        { skipGlobalPermissionAlert: true }
      );
      return response.data;
    } catch (error) {
      console.error("Bir sorun oluştu:", error.response?.data || error.message);
      throw error;
    }
  };

  formAnswerPublic = async (data) => {
    try {
      const response = await axiosInstance.post(
        API_ENDPOINTS.PUBLIC.FORM_ANSWER,
        data,
        { skipGlobalPermissionAlert: true }
      );
      return response.data;
    } catch (error) {
      console.error("Bir sorun oluştu:", error.response?.data || error.message);
      throw error;
    }
  };
  getFormInvitations = async (formId) => {
    try {
      const response = await axiosInstance.get(
        API_ENDPOINTS.FORMS.BY_ID(formId)
      );
      // Backend'ten gelen patients array'inden tüm invitations'ları topla
      const allInvitations = [];
      if (response.data.patients) {
        response.data.patients.forEach(patient => {
          if (patient.invitations && patient.invitations.length > 0) {
            allInvitations.push(...patient.invitations);
          }
        });
      }
      return allInvitations;
    } catch (error) {
      console.error("Bir sorun oluştu:", error.response?.data || error.message);
      throw error;
    }
  };

  getPatientInvitations = async (patientId, options = {}) => {
    try {
      const params = {};
      if (options?.fileId) {
        params.fileId = options.fileId;
      }
      const response = await axiosInstance.get(
        API_ENDPOINTS.PATIENTS.INVITATIONS(patientId),
        { params: Object.keys(params).length > 0 ? params : undefined }
      );
      return response.data;
    } catch (error) {
      console.error("Bir sorun oluştu:", error.response?.data || error.message);
      throw error;
    }
  };
}
